import { normalizeAbsolutePath } from '../../../vfs/paths';
import { WORKSPACE_PAGES } from './page_catalog_definitions.helpers';

/** Only explicit HTTP(S) links with no embedded credentials may leave the app. */
export function validNavigationUrl(value: string): string | null {
  if (value.length > 2048) return null;
  try {
    const url = new URL(value);
    if (
      !['http:', 'https:'].includes(url.protocol) ||
      !url.hostname ||
      url.username ||
      url.password
    )
      return null;
    return url.href;
  } catch {
    return null;
  }
}

/** Exact app routes obtained from an authorized result or supplied by the user. */
export function validAppRoute(value: string, orgSlug: string): string | null {
  if (
    value.length > 2048 ||
    !value.startsWith('/') ||
    value.startsWith('//') ||
    /[\\\u0000-\u001f]/.test(value)
  )
    return null;
  try {
    const url = new URL(value, 'https://app.invalid');
    const orgPrefix = `/${encodeURIComponent(orgSlug)}`;
    if (
      url.origin !== 'https://app.invalid' ||
      !(
        url.pathname === orgPrefix ||
        url.pathname.startsWith(`${orgPrefix}/`) ||
        url.pathname === '/shared' ||
        url.pathname.startsWith('/settings/')
      )
    )
      return null;
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return null;
  }
}

export function filesHref(orgSlug: string, path: string): string {
  const prefix = `/${orgSlug}/`;
  if (!path.startsWith(prefix)) throw new Error('FILE_PATH_OUTSIDE_WORKSPACE');
  const segments = path
    .slice(prefix.length)
    .split('/')
    .filter(Boolean)
    .map((part) =>
      part.startsWith('@')
        ? `@${encodeURIComponent(part.slice(1))}`
        : encodeURIComponent(part)
    );
  return `/${encodeURIComponent(orgSlug)}/files/${segments.join('/')}`;
}

export type NavigationRouteDestination =
  | { kind: 'file'; path: string; href: string }
  // A product or avatar page that opens the workspace folder at `path`.
  | { kind: 'entity'; path: string; href: string }
  | { kind: 'thread'; reference: string; href: string }
  | { kind: 'page'; href: string };

/**
 * Product and avatar pages name the folder they open in a query parameter.
 * Returns undefined for other routes and null for a malformed folder path.
 */
function entityRoutePath(
  url: URL,
  segments: readonly string[],
  orgSlug: string
): string | null | undefined {
  const param =
    segments[1] === 'products'
      ? 'productPath'
      : segments[1] === 'files' &&
          segments[2] === 'avatars' &&
          segments.length === 4
        ? 'avatarPath'
        : null;
  const value = param ? url.searchParams.get(param) : null;
  if (value === null) return undefined;
  const prefix = `/${orgSlug}/`;
  if (!value.startsWith(prefix) || /[\\\u0000-\u001f]/.test(value)) return null;
  const path = normalizeAbsolutePath(value);
  return path.startsWith(prefix) ? path : null;
}

/** The route, rather than stored presentation kind, selects authorization. */
export function classifyNavigationRoute(
  value: string,
  orgSlug: string
): NavigationRouteDestination | null {
  const href = validAppRoute(value, orgSlug);
  if (!href) return null;
  try {
    const url = new URL(href, 'https://app.invalid');
    const segments = url.pathname
      .split('/')
      .filter(Boolean)
      .map(decodeURIComponent);
    if (segments.some((segment) => /[\\/\u0000-\u001f]/.test(segment)))
      return null;
    if (segments[0] !== orgSlug) return { kind: 'page', href };
    const entityPath = entityRoutePath(url, segments, orgSlug);
    if (entityPath !== undefined)
      return entityPath ? { kind: 'entity', path: entityPath, href } : null;
    const route = `/${segments.slice(1).join('/')}`;
    if (segments[1] === 'threads' && segments.length > 2) {
      if (segments.length !== 3) return null;
      return { kind: 'thread', reference: segments[2]!, href };
    }
    if (segments[1] === 'files') {
      const catalogPage = WORKSPACE_PAGES.some((page) => page.route === route);
      // Node references change the backing resource even on a catalog route.
      // Navigation uses the user's requested path and drops that alternate selector.
      if (catalogPage && !url.searchParams.has('node-reference'))
        return { kind: 'page', href };
      const pathSegments = segments.slice(2);
      if (pathSegments[0] === orgSlug) pathSegments.shift();
      const path = `/${orgSlug}/${pathSegments.join('/')}`;
      return { kind: 'file', path, href: filesHref(orgSlug, path) };
    }
    return { kind: 'page', href };
  } catch {
    return null;
  }
}

/** Match exact application origins, never wildcard auth or storage origins. */
export function firstPartyNavigationRoute(value: string): string | null {
  const href = validNavigationUrl(value);
  if (!href) return null;
  const origins = new Set(['https://app.imai.studio', 'http://localhost:3000']);
  // Invitation and billing URL builders use these application URL settings.
  for (const configured of [
    process.env.APP_URL,
    process.env.NEXT_PUBLIC_APP_URL,
    process.env.SITE_URL,
    process.env.NEXT_PUBLIC_SITE_URL,
  ]) {
    const applicationUrl = configured ? validNavigationUrl(configured) : null;
    if (applicationUrl) origins.add(new URL(applicationUrl).origin);
  }
  const url = new URL(href);
  return origins.has(url.origin)
    ? `${url.pathname}${url.search}${url.hash}`
    : null;
}
